import { useState, useCallback, useEffect, useRef } from 'react'
import TranslatableText from './TranslatableText.jsx'
import { readAdminToken } from '../services/adminAuth.js'
import { readPastorToken } from '../services/pastorAuth.js'

const API = import.meta.env.VITE_API_URL || ''

const CATEGORY_COLORS = {
  Love:         '#e91e8c',
  Work:         '#1e88e5',
  Family:       '#43a047',
  Friendship:   '#8e24aa',
  Prayer:       '#d4a72c',
  Other:        '#546e7a',
}

const CATEGORY_EMOJIS = {
  Love:       '❤️',
  Work:       '💼',
  Family:     '🏠',
  Friendship: '🤝',
  Prayer:     '🙏',
  Other:      '🕯',
}

const REPORT_REASONS = ['Hate speech', 'Personal data', 'Spam', 'Violence', 'Other']

// Per-thread pseudonymous identity (Jodel-style). A comment's author_tag maps
// deterministically to one emoji + color so readers can tell participants apart
// within a thread without any accounts.
const AUTHOR_EMOJIS = ['🦊', '🐼', '🦉', '🐙', '🦁', '🐺', '🦝', '🐸', '🐵', '🦅', '🐢', '🦄', '🐳', '🦓', '🦔', '🐝']
const AUTHOR_COLORS = ['#e91e8c', '#1e88e5', '#43a047', '#fb8c00', '#8e24aa', '#00897b', '#c0392b', '#3949ab', '#7cb342', '#546e7a']

function authorVisual(tag) {
  if (!tag) return { emoji: '👤', color: '#546e7a' }
  let h = 0
  for (let i = 0; i < tag.length; i++) h = (h * 31 + tag.charCodeAt(i)) >>> 0
  return {
    emoji: AUTHOR_EMOJIS[h % AUTHOR_EMOJIS.length],
    color: AUTHOR_COLORS[h % AUTHOR_COLORS.length],
  }
}

// Augen-Icons: geschlossen = ausblenden, offen = wieder einblenden
function EyeIcon({ open }) {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      {open ? (
        <>
          <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12z" />
          <circle cx="12" cy="12" r="3" />
        </>
      ) : (
        <>
          <path d="M2 10s3.5 5 10 5 10-5 10-5" />
          <path d="M4.5 13 3 15M9 14.7 8.3 17M15 14.7l.7 2.3M19.5 13l1.5 2" />
        </>
      )}
    </svg>
  )
}

// Urteil: vor der eigenen Stimme nur die Anzahl, danach das Ergebnis (Reveal)
function Verdict({ absolve = 0, guilty = 0, mine, pending, onJudge }) {
  const total = absolve + guilty
  const pct = total ? Math.round((absolve / total) * 100) : 0
  return (
    <div className={`verdict ${mine ? 'judged' : ''}`}>
      <button
        className={`verdict-btn absolve ${mine === 'absolve' ? 'active' : ''}`}
        onClick={() => onJudge('absolve')}
        disabled={pending}
        aria-pressed={mine === 'absolve'}
      >
        ✝ Absolve
      </button>
      <div className="verdict-result" aria-live="polite">
        {mine ? (
          <>
            <div className="verdict-bar" role="img" aria-label={`${pct}% absolved`}>
              <span className="verdict-bar-fill" style={{ width: `${pct}%` }} />
            </div>
            <span className="verdict-label">
              {pct >= 50 ? `${pct}% absolved` : `${100 - pct}% guilty`} · {total}
            </span>
          </>
        ) : (
          <span className="verdict-label">
            {total ? `${total} ${total === 1 ? 'verdict' : 'verdicts'} · judge to see` : 'Be the first to judge'}
          </span>
        )}
      </div>
      <button
        className={`verdict-btn guilty ${mine === 'guilty' ? 'active' : ''}`}
        onClick={() => onJudge('guilty')}
        disabled={pending}
        aria-pressed={mine === 'guilty'}
      >
        Guilty ⚖
      </button>
    </div>
  )
}

function timeAgo(dateStr) {
  const diff = Date.now() - new Date(dateStr).getTime()
  const mins  = Math.floor(diff / 60000)
  const hours = Math.floor(diff / 3600000)
  const days  = Math.floor(diff / 86400000)
  if (mins < 1)   return 'just now'
  if (mins < 60)  return `${mins}m ago`
  if (hours < 24) return `${hours}h ago`
  return `${days}d ago`
}

// hidden: im normalen Feed eingeklappt; showWhenHidden: im Hidden-Reiter trotzdem voll anzeigen
export default function ConfessionCard({ confession, onVote, onJudge, onPray, onReport, onPin, focus, hidden, showWhenHidden, onToggleHidden, onOpen, defaultCommentsOpen }) {
  const cardRef = useRef(null)
  const [highlighted, setHighlighted] = useState(false)
  const [reportOpen, setReportOpen]   = useState(false)
  const [reportSent, setReportSent]   = useState(false)
  const [menuOpen, setMenuOpen]       = useState(false)
  const menuRef = useRef(null)
  const reportRef = useRef(null)
  const [votePending, setVotePending] = useState(false)
  const [judgePending, setJudgePending] = useState(false)
  const [prayPending, setPrayPending] = useState(false)
  const [commentsOpen, setCommentsOpen] = useState(false)
  const [comments, setComments]       = useState(null)  // null = not loaded yet
  const [commentText, setCommentText] = useState('')
  const [commentError, setCommentError] = useState(null)
  const [commentSubmitting, setCommentSubmitting] = useState(false)
  const [localCount, setLocalCount]   = useState(confession.comment_count ?? 0)

  // ⋯-Menü schließt bei Klick daneben oder Escape
  useEffect(() => {
    if (!menuOpen) return
    const onDown = e => { if (!menuRef.current?.contains(e.target)) setMenuOpen(false) }
    const onKey  = e => { if (e.key === 'Escape') setMenuOpen(false) }
    document.addEventListener('pointerdown', onDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('pointerdown', onDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [menuOpen])

  // Report gewählt → zu den Gründen unten am Post scrollen
  useEffect(() => {
    if (!reportOpen) return
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    reportRef.current?.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'nearest' })
    reportRef.current?.querySelector('.reason-btn')?.focus({ preventScroll: true })
  }, [reportOpen])

  // Einzelansicht: Kommentare direkt aufgeklappt
  useEffect(() => {
    if (defaultCommentsOpen) {
      setCommentsOpen(true)
      loadComments()
    }
  }, [defaultCommentsOpen]) // eslint-disable-line react-hooks/exhaustive-deps

  // Nach einem Refresh die neue Kommentaranzahl übernehmen
  useEffect(() => {
    setLocalCount(confession.comment_count ?? 0)
  }, [confession.comment_count])

  const { id, text, category, karma_score, is_pinned, created_at, pinned_until } = confession
  const isPinActive = is_pinned && pinned_until && new Date(pinned_until) > new Date()
  // Kurze Geständnisse groß auf der Kachel, lange kleiner und linksbündig
  const tileSize = text.length <= 90 ? 'short' : text.length <= 240 ? 'medium' : 'long'

  async function handleVote(type) {
    if (votePending) return
    setVotePending(true)
    await onVote(id, type)
    setVotePending(false)
  }

  async function handleJudge(verdict) {
    if (judgePending) return
    setJudgePending(true)
    await onJudge(id, verdict)
    setJudgePending(false)
  }

  async function handlePray() {
    if (prayPending) return
    setPrayPending(true)
    await onPray(id)
    setPrayPending(false)
  }

  async function handleReport(reason) {
    try {
      await onReport(id, reason)
      setReportSent(true)
      setReportOpen(false)
    } catch (err) {
      console.error(err)
    }
  }

  const loadComments = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/confessions/${id}/comments`)
      const data = await res.json()
      setComments(Array.isArray(data) ? data : [])
    } catch {
      setComments([])
    }
  }, [id])

  const toggleComments = useCallback(async () => {
    if (!commentsOpen && comments === null) await loadComments()
    setCommentsOpen(o => !o)
  }, [commentsOpen, comments, loadComments])

  // Aus Push-Nachricht geöffnet: hinscrollen, kurz hervorheben und bei
  // Kommentaren die frisch geladenen Kommentare aufklappen
  useEffect(() => {
    if (!focus) return
    if (focus.type === 'comment') {
      setCommentsOpen(true)
      loadComments()
    }
    cardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })
    setHighlighted(true)
    const t = setTimeout(() => setHighlighted(false), 2000)
    return () => clearTimeout(t)
  }, [focus?.nonce]) // eslint-disable-line react-hooks/exhaustive-deps

  async function handleCommentSubmit(e) {
    e.preventDefault()
    const trimmed = commentText.trim()
    if (!trimmed) return
    setCommentSubmitting(true)
    setCommentError(null)
    try {
      // Admin oder eingeloggter Seelsorger antworten nicht anonym
      const adminToken = readAdminToken() || readPastorToken()
      const res = await fetch(`${API}/api/confessions/${id}/comments`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(adminToken ? { Authorization: `Bearer ${adminToken}` } : {}),
        },
        body: JSON.stringify({ text: trimmed }),
      })
      const data = await res.json()
      if (!res.ok) throw new Error(data.error || 'Error posting comment.')
      setComments(prev => [...(prev || []), data])
      setLocalCount(c => c + 1)
      setCommentText('')
    } catch (err) {
      setCommentError(err.message)
    } finally {
      setCommentSubmitting(false)
    }
  }

  const eyeButton = (
    <button
      className={`eye-btn ${hidden ? 'is-hidden' : ''}`}
      onClick={() => onToggleHidden(id)}
      aria-label={hidden ? 'Reveal confession' : 'Hide confession'}
      title={hidden ? 'Reveal' : 'Hide'}
    >
      <EyeIcon open={hidden} />
    </button>
  )

  if (hidden && !showWhenHidden) {
    return (
      <article ref={cardRef} className="confession-card collapsed">
        <span
          className="category-badge"
          style={{ '--badge-color': CATEGORY_COLORS[category] || '#546e7a' }}
        >
          {category}
        </span>
        <span className="collapsed-label">Hidden confession</span>
        {eyeButton}
      </article>
    )
  }

  return (
    <article
      ref={cardRef}
      className={`confession-card post ${isPinActive ? 'pinned' : ''} ${highlighted ? 'focused' : ''}`}
      style={{ '--cat-color': CATEGORY_COLORS[category] || '#546e7a' }}
    >
      {/* Kopfzeile wie bei Instagram: Avatar, "Name", Kategorie, Menü */}
      <header className="post-header">
        <span className="post-avatar" aria-hidden="true">{CATEGORY_EMOJIS[category] || '🕯'}</span>
        <div className="post-author">
          <span className="post-name">Anonymous</span>
          <span className="post-sub">
            {category}
            {isPinActive && <span className="pin-badge" title="Pinned confession">📌 Pinned</span>}
          </span>
        </div>
        {eyeButton}
        <div className="post-menu-wrap" ref={menuRef}>
          <button
            className={`post-more ${menuOpen ? 'active' : ''}`}
            onClick={() => setMenuOpen(o => !o)}
            aria-haspopup="menu"
            aria-expanded={menuOpen}
            aria-label="More options"
          >
            ⋯
          </button>
          {menuOpen && (
            <div className="post-menu" role="menu">
              <button
                role="menuitem"
                className="post-menu-item danger"
                disabled={reportSent}
                onClick={() => { setMenuOpen(false); setReportOpen(true) }}
              >
                {reportSent ? 'Reported ✓' : '⚑ Report'}
              </button>
              <button
                role="menuitem"
                className="post-menu-item"
                onClick={() => setMenuOpen(false)}
              >
                Cancel
              </button>
            </div>
          )}
        </div>
      </header>

      {/* Das "Bild" des Posts: der Text auf einer Kachel in Kategoriefarbe */}
      {/* Antippen öffnet den Post unter seiner eigenen URL (/p/<slug>) */}
      <div
        className={`post-tile ${tileSize} ${onOpen ? 'clickable' : ''}`}
        onClick={onOpen ? (e => {
          // Klicks auf "See translation" & Co. öffnen den Post nicht
          if (e.target.closest('button, a')) return
          onOpen(confession)
        }) : undefined}
      >
        <TranslatableText as="p" className="card-text" text={text} />
      </div>

      <div className="post-actions">
        <button
          className={`post-icon pray-btn ${confession.my_prayer ? 'active' : ''}`}
          onClick={handlePray}
          disabled={prayPending}
          aria-pressed={!!confession.my_prayer}
          aria-label={confession.my_prayer ? "You're praying for this" : "I'm praying for you"}
          title={confession.my_prayer ? "You're praying for this" : "I'm praying for you"}
        >
          🙏
        </button>
        <button
          className={`post-icon comment-btn ${commentsOpen ? 'active' : ''}`}
          onClick={toggleComments}
          aria-expanded={commentsOpen}
          aria-label="Comments"
        >
          💬
        </button>
        <div className="vote-controls">
          <button
            className="vote-btn vote-up"
            onClick={() => handleVote('up')}
            disabled={votePending}
            aria-label="Upvote"
          >
            ▲
          </button>
          <span className={`karma-score ${karma_score > 0 ? 'positive' : karma_score < 0 ? 'negative' : ''}`}>
            {karma_score}
          </span>
          <button
            className="vote-btn vote-down"
            onClick={() => handleVote('down')}
            disabled={votePending}
            aria-label="Downvote"
          >
            ▼
          </button>
        </div>
        {!isPinActive && (
          <button
            className="post-icon pin-btn"
            onClick={() => onPin(confession)}
            aria-label="Pin confession for €0.99"
            title="Pin confession for €0.99"
          >
            📌
          </button>
        )}
      </div>

      <div className="post-body">
        {confession.prayer_count > 0 && (
          <p className="post-likes">
            {confession.prayer_count} {confession.prayer_count === 1 ? 'prayer' : 'prayers'}
          </p>
        )}

        {/* Gebetsanliegen werden nicht verurteilt – nur Gebete */}
        {category !== 'Prayer' && (
          <Verdict
            absolve={confession.absolve_count ?? 0}
            guilty={confession.guilty_count ?? 0}
            mine={confession.my_verdict}
            pending={judgePending}
            onJudge={handleJudge}
          />
        )}

        {localCount > 0 && !commentsOpen && (
          <button className="post-view-comments" onClick={onOpen ? () => onOpen(confession) : toggleComments}>
            View {localCount === 1 ? '1 comment' : `all ${localCount} comments`}
          </button>
        )}

        {onOpen && confession.slug ? (
          <a
            className="card-time post-permalink"
            href={`/p/${confession.slug}`}
            onClick={e => {
              if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return
              e.preventDefault()
              onOpen(confession)
            }}
          >
            <time dateTime={created_at}>{timeAgo(created_at)}</time>
          </a>
        ) : (
          <time className="card-time" dateTime={created_at}>
            {timeAgo(created_at)}
          </time>
        )}
        {reportSent && <span className="report-sent">Reported ✓</span>}
      </div>

      {commentsOpen && (
        <div className="comment-panel">
          {comments === null ? (
            <div className="comment-loading">Loading…</div>
          ) : (
            <>
              {comments.length === 0 && (
                <p className="comment-empty">No comments yet. Be the first.</p>
              )}
              <ul className="comment-list">
                {comments.map(c => {
                  const staff = c.is_admin || !!c.pastor_name
                  const v = staff
                    ? { emoji: '✝', color: '#d4a72c' }
                    : authorVisual(c.author_tag)
                  return (
                    <li key={c.id} className="comment-item">
                      <span
                        className={`comment-author ${c.is_op ? 'is-op' : ''} ${staff ? 'is-admin' : ''}`}
                        style={{ '--author-color': v.color }}
                        title={c.pastor_name ? `${c.pastor_name} (verified pastor)` : c.is_admin ? 'Reverend (Admin)' : c.is_op ? 'Original poster' : 'Participant'}
                      >
                        <span className="comment-avatar">{v.emoji}</span>
                        {c.pastor_name && <span className="pastor-name">{c.pastor_name}</span>}
                        {c.pastor_name
                          ? <span className="op-badge">PASTOR</span>
                          : c.is_admin
                            ? <span className="op-badge">ADMIN</span>
                            : c.is_op && <span className="op-badge">OP</span>}
                      </span>
                      <TranslatableText as="span" className="comment-text" text={c.text} />
                      <time className="comment-time">{timeAgo(c.created_at)}</time>
                    </li>
                  )
                })}
              </ul>

              <form className="comment-input-row" onSubmit={handleCommentSubmit}>
                <input
                  className="comment-input"
                  type="text"
                  placeholder="Add a comment…"
                  maxLength={200}
                  value={commentText}
                  onChange={e => setCommentText(e.target.value)}
                  disabled={commentSubmitting}
                />
                <button
                  className="btn btn-primary comment-submit"
                  type="submit"
                  disabled={commentSubmitting || !commentText.trim()}
                >
                  {commentSubmitting ? '…' : 'Post'}
                </button>
              </form>
              {commentError && (
                <p className="comment-error">{commentError}</p>
              )}
            </>
          )}
        </div>
      )}

      {reportOpen && (
        <div ref={reportRef} className="report-panel" role="dialog" aria-label="Report">
          <p className="report-label">Reason:</p>
          <div className="report-reasons">
            {REPORT_REASONS.map(r => (
              <button
                key={r}
                className="reason-btn"
                onClick={() => handleReport(r)}
              >
                {r}
              </button>
            ))}
          </div>
          <button className="action-btn" onClick={() => setReportOpen(false)}>
            Cancel
          </button>
        </div>
      )}
    </article>
  )
}
